import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Newspaper, ChevronRight, Eye, BarChart3, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContentPulseData {
  news: { publishedThisWeek: number; totalViews: number };
  surveys: { active: number; totalResponses: number; latestSurveyTitle: string };
  events: { thisWeek: number; upcoming: number };
}

export default function ContentPulseWidget() {
  const [, navigate] = useLocation();

  const { data } = useQuery<ContentPulseData>({
    queryKey: ["/api/dashboard/content-pulse"],
  });

  const items = data ? [
    {
      label: 'Articles This Week',
      value: data.news.publishedThisWeek,
      sub: `${data.news.totalViews} total views`,
      icon: Newspaper,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-950/30',
      path: '/news',
    },
    {
      label: 'Active Surveys',
      value: data.surveys.active,
      sub: `${data.surveys.totalResponses} responses`,
      icon: BarChart3,
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950/30',
      path: '/surveys',
    },
    {
      label: 'Events Coming Up',
      value: data.events.upcoming,
      sub: `${data.events.thisWeek} this week`,
      icon: Calendar,
      color: 'text-teal-600 dark:text-teal-400',
      bgColor: 'bg-teal-50 dark:bg-teal-950/30',
      path: '/events',
    },
  ] : [];

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-md">
              <Newspaper className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Content Pulse</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/news")}
          >
            Manage <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {!data ? (
          <div className="text-center py-6 text-muted-foreground">
            <Newspaper className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Loading content data...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(({ label, value, sub, icon: Icon, color, bgColor, path }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                className={cn("w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors hover:opacity-80", bgColor)}
              >
                <div className={cn("p-2 rounded-lg", color)}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
                <span className={cn("text-2xl font-bold", color)}>{value}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
